import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiErr } from "@/lib/api";
import { Reveal } from "@/components/Motion";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { IMAGES } from "@/data";

export default function Auth({ mode = "login" }) {
  const [m, setM] = useState(mode);
  const { login, register } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ first_name: "", last_name: "", email: "", phone: "", password: "", email_consent: true, sms_consent: false });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      if (m === "login") { const u = await login(f.email, f.password); nav(u.role === "admin" ? "/admin" : "/saved"); }
      else if (m === "signup") { await register(f); nav("/saved"); }
      else { setErr(""); const { api } = await import("@/lib/api"); await api.post("/auth/forgot-password", { email: f.email }); setErr("If an account exists, reset instructions have been sent."); }
    } catch (e2) { setErr(apiErr(e2.response?.data?.detail) || "Something went wrong."); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-cream">
      <div className="hidden lg:block relative">
        <img src={IMAGES.interior1} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-navy/70" />
        <div className="absolute bottom-12 left-12 text-cream max-w-sm">
          <h2 className="font-serif text-4xl mb-3">Save homes. Track requests. Move with confidence.</h2>
          <p className="text-cream/70">Create a free account to save listings and manage your journey.</p>
        </div>
      </div>
      <div className="flex items-center justify-center px-6 py-28">
        <Reveal className="w-full max-w-md">
          <Link to="/" className="font-serif text-2xl text-navy">Georgia Home Connect</Link>
          <h1 className="font-serif text-4xl text-navy tracking-tight mt-8 mb-6">
            {m === "login" ? "Welcome back" : m === "signup" ? "Create your account" : "Reset your password"}
          </h1>
          <form onSubmit={submit} className="space-y-4">
            {m === "signup" && (
              <div className="grid grid-cols-2 gap-4">
                <div><Label>First name</Label><Input value={f.first_name} onChange={set("first_name")} data-testid="auth-first" required className="bg-white" /></div>
                <div><Label>Last name</Label><Input value={f.last_name} onChange={set("last_name")} data-testid="auth-last" required className="bg-white" /></div>
              </div>
            )}
            <div><Label>Email</Label><Input type="email" value={f.email} onChange={set("email")} data-testid="auth-email" required className="bg-white" /></div>
            {m === "signup" && <div><Label>Phone</Label><Input value={f.phone} onChange={set("phone")} data-testid="auth-phone" className="bg-white" /></div>}
            {m !== "forgot" && <div><Label>Password</Label><Input type="password" value={f.password} onChange={set("password")} data-testid="auth-password" required className="bg-white" /></div>}
            {m === "signup" && (
              <div className="flex items-start gap-3 pt-1">
                <Checkbox id="sms" checked={f.sms_consent} onCheckedChange={set("sms_consent")} data-testid="auth-sms" className="mt-1" />
                <label htmlFor="sms" className="text-xs text-charcoal/60">I agree to receive calls/texts about my inquiry. Message & data rates may apply. Reply STOP to opt out.</label>
              </div>
            )}
            {err && <p className="text-sm text-destructive" data-testid="auth-error">{err}</p>}
            <Button type="submit" disabled={loading} data-testid="auth-submit" className="w-full bg-gold text-navy font-semibold rounded-full h-12">
              {loading ? "Please wait…" : m === "login" ? "Sign In" : m === "signup" ? "Create Account" : "Send Reset Link"}
            </Button>
          </form>
          <div className="mt-6 text-sm text-charcoal/60 space-y-2">
            {m === "login" && <><p>New here? <button onClick={() => setM("signup")} data-testid="switch-signup" className="text-gold font-medium">Create an account</button></p>
              <p><button onClick={() => setM("forgot")} data-testid="switch-forgot" className="text-gold font-medium">Forgot password?</button></p></>}
            {m === "signup" && <p>Already have an account? <button onClick={() => setM("login")} data-testid="switch-login" className="text-gold font-medium">Sign in</button></p>}
            {m === "forgot" && <p><button onClick={() => setM("login")} className="text-gold font-medium">Back to sign in</button></p>}
          </div>
        </Reveal>
      </div>
    </div>
  );
}
